/**
 * Moving, scaling and rotating a placement.
 *
 * All of it in FACE-LOCAL space, and that is the decision the tool rests on.
 * A `FacePlacement` stores `{face, x, y, scale, rotation}` where x and y are
 * 0..1 within the face and scale is a fraction of the face's width — never
 * template pixels. Doing the drag arithmetic in pixels and converting back at
 * the end is the obvious approach and it is wrong twice: the rounding drifts
 * over a long gesture, and the stored value stops meaning "40% across the
 * chest", which is the whole reason a logo survives the garment changing shape.
 *
 * ## Every gesture is computed from its start, never from the last frame
 *
 * Each function here takes the placement **as it was when the pointer went
 * down** and the pointer's position **now**. It does not take the live
 * placement and a delta. Accumulating frame to frame is how a rotation ends up
 * a degree off after a slow drag, and how a scale that goes out and comes back
 * fails to return to where it started — the same class of error the zoom factor
 * in view.ts is shaped to avoid.
 *
 * The one thing not done here is clipping: `layers.ts` clips a placement to its
 * face before rotating it, so nothing in this file has to reason about a
 * rotated quad against a rectangle.
 */
import type { FacePlacement, RegionId } from "../../shared/format";
import {
  cornersOf,
  nearestFace,
  type PlacedRect,
  type Point,
  resolvePlacement,
  toFaceCoords,
} from "../../shared/placement";
import { REGIONS } from "../../shared/regions";

/** What the pointer has hold of. */
export type Handle = "move" | "nw" | "ne" | "se" | "sw" | "rotate";

/** Corner order matches `cornersOf`: top-left, top-right, bottom-right, bottom-left. */
const CORNERS: Handle[] = ["nw", "ne", "se", "sw"];

/**
 * How far above the top edge the rotation grip floats, in template pixels.
 *
 * Far enough not to be mistaken for a corner, near enough to stay on screen
 * when a placement is against the top of its face.
 */
export const GRIP_REACH = 22;

/** Scale limits. Below the floor a placement is unfindable; above the ceiling
 *  it is entirely clipped away by its own face and looks like a bug. */
const MIN_SCALE = 0.02;
const MAX_SCALE = 2;

/** Where the rotation grip sits, in template pixels. */
export function gripOf(r: PlacedRect, reach = GRIP_REACH): Point {
  const cos = Math.cos(r.rotation);
  const sin = Math.sin(r.rotation);
  const dy = -(r.h / 2 + reach);
  return { x: r.cx - dy * sin, y: r.cy + dy * cos };
}

/** A point in the rect's own frame, with the rotation taken back out. */
function toLocal(r: PlacedRect, x: number, y: number): Point {
  const cos = Math.cos(-r.rotation);
  const sin = Math.sin(-r.rotation);
  const dx = x - r.cx;
  const dy = y - r.cy;
  return { x: dx * cos - dy * sin, y: dx * sin + dy * cos };
}

/**
 * What a press at this point grabs, or null for nothing.
 *
 * Handles are tested before the body, because they overlap it — a corner sits
 * on the outline, and a press there means "resize" rather than "move" in every
 * editor anybody has used. `tolerance` is in template pixels, so the caller
 * divides its screen-space hit radius by the zoom: a handle has to stay the
 * same size under the finger at 1x and at 32x.
 */
export function hitTest(
  p: FacePlacement,
  aspect: number,
  x: number,
  y: number,
  tolerance: number,
): Handle | null {
  const r = resolvePlacement(p, aspect);
  const near = (pt: Point) => Math.hypot(pt.x - x, pt.y - y) <= tolerance;

  if (near(gripOf(r))) return "rotate";

  const corners = cornersOf(r);
  for (let i = 0; i < corners.length; i++) {
    if (near(corners[i] as Point)) return CORNERS[i] as Handle;
  }

  const local = toLocal(r, x, y);
  if (Math.abs(local.x) <= r.w / 2 && Math.abs(local.y) <= r.h / 2) return "move";
  return null;
}

/** Keep a placement's centre on its own face. See `scaleToFit`: a centre that
 *  has left the face cannot be rescued by scaling. */
const clamp01 = (v: number) => Math.min(1, Math.max(0, v));

/**
 * Move, from a template-pixel delta.
 *
 * Divided by the face's own width and height rather than by a single number:
 * a limb strip is 64 by 128, so one delta in pixels is two different fractions.
 */
export function movedBy(start: FacePlacement, dx: number, dy: number): FacePlacement {
  const from = REGIONS[start.face];

  /*
    Where the centre wants to be, in template pixels, UNCLAMPED.

    The clamp has to come after the face is decided. Clamping first — which is
    what this did — meant the centre could never leave its own face, and since
    the face is only ever set when an image is first placed, that made it
    permanent: an image dropped on the chest could not be moved to the back by
    any means the editor offered. It was not a limitation anybody chose; it was
    a scaling rule borrowed for movement, where it does not belong.
  */
  const cx = from.x + start.x * from.w + dx;
  const cy = from.y + start.y * from.h + dy;

  /*
    `nearestFace`, not `regionAt`. 54.9% of this template is dead space, so a
    centre dragged between two faces is usually over nothing at all — and
    "nothing" is not somewhere a placement can live. The nearest face is what a
    user dragging in that direction meant.
  */
  const face = nearestFace(cx, cy);
  const to = REGIONS[face];
  const local = toFaceCoords(face, cx, cy);

  /*
    Scale is a fraction of the face's WIDTH, so carrying the number across
    resizes the image. The chest is 128 texels wide and a sleeve is 64: the
    same 0.5 is a 64px badge on one and a 32px badge on the other. Converting
    keeps the badge the size it looks, which is what somebody dragging it
    expects — the number is an implementation detail and the size is not.
  */
  const scale =
    face === start.face
      ? start.scale
      : Math.min(MAX_SCALE, Math.max(MIN_SCALE, (start.scale * from.w) / to.w));

  return { ...start, face, x: clamp01(local.x), y: clamp01(local.y), scale };
}

/**
 * Scale about the centre, from where the corner was grabbed to where the
 * pointer is now.
 *
 * About the centre rather than about the opposite corner, which is the other
 * common choice. On a face the size of a sleeve, anchoring a corner walks the
 * placement across the face as you resize it, and then it has to be moved back
 * — two gestures to do one thing. Centred scaling keeps it where it was put.
 *
 * Rotation-agnostic on purpose: distance from the centre does not care which
 * way the placement is turned, so this is one line rather than a case per
 * corner.
 */
export function scaledTo(
  start: FacePlacement,
  aspect: number,
  grab: Point,
  now: Point,
): FacePlacement {
  const r = resolvePlacement(start, aspect);
  const was = Math.hypot(grab.x - r.cx, grab.y - r.cy);
  // A grab exactly on the centre has no direction to scale along.
  if (was < 1e-6) return start;
  const is = Math.hypot(now.x - r.cx, now.y - r.cy);
  const next = start.scale * (is / was);
  return { ...start, scale: Math.min(MAX_SCALE, Math.max(MIN_SCALE, next)) };
}

/** Snap increment for a held modifier: 15°, the usual one. */
export const SNAP = Math.PI / 12;

/** Normalised to (-π, π], so a saved rotation never accumulates turns. */
export function normaliseAngle(a: number): number {
  const twoPi = Math.PI * 2;
  let out = ((((a + Math.PI) % twoPi) + twoPi) % twoPi) - Math.PI;
  // (-π, π] rather than [-π, π): exactly half a turn reads better as +180°.
  if (out <= -Math.PI) out += twoPi;
  return out;
}

/**
 * Rotate about the centre, by however far the pointer has swung since the grab.
 *
 * Snapped to 15° when asked. Snapping the RESULT rather than the delta is what
 * makes the snap absolute — otherwise a placement that began at 7° stays 7° off
 * every detent, which feels like the snap is broken.
 */
export function rotatedTo(
  start: FacePlacement,
  aspect: number,
  grab: Point,
  now: Point,
  snap = false,
): FacePlacement {
  const r = resolvePlacement(start, aspect);
  const a0 = Math.atan2(grab.y - r.cy, grab.x - r.cx);
  const a1 = Math.atan2(now.y - r.cy, now.x - r.cx);
  let next = start.rotation + (a1 - a0);
  if (snap) next = Math.round(next / SNAP) * SNAP;
  return { ...start, rotation: normaliseAngle(next) };
}

/**
 * Nudge by whole template pixels, for the arrow keys.
 *
 * Keyboard nudging is the only way to place something exactly when a face is
 * 64 pixels wide and the pointer is a finger.
 */
export function nudged(start: FacePlacement, dx: number, dy: number): FacePlacement {
  return movedBy(start, dx, dy);
}

/** The face a placement would belong to if its centre were taken literally. */
export function centreOf(p: FacePlacement): Point {
  const face = REGIONS[p.face as RegionId];
  return { x: face.x + p.x * face.w, y: face.y + p.y * face.h };
}
